import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DECEASED_ARTIST_TAG } from "@/lib/tina/artist-audit";
import { DeceasedDetails } from "@/pages/tina/audits/artist/components/deceased-details";
import type { ArtistAuditFindingRow } from "@/types/tina";

interface ApproveDeceasedDialogProps {
  /** The finding to approve; `null` closes the dialog. */
  finding: ArtistAuditFindingRow | null;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * Confirm an Approve on a "possibly deceased" card. It writes to the Mood artist and to
 * tina's denylist, so it says exactly what will happen — and what will not.
 *
 * The Wikidata link comes first again: Wikidata is open-edit, and an approved living act
 * would be blocked from every new event.
 */
export function ApproveDeceasedDialog({
  finding,
  pending,
  onOpenChange,
  onConfirm,
}: ApproveDeceasedDialogProps) {
  const name = finding?.evidence.artists?.[0]?.name ?? "this artist";
  return (
    <Dialog open={finding !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Approve: {name} has died?</DialogTitle>
          <DialogDescription>
            Check the date of death on Wikidata first. It is open-edit and can
            be wrong.
          </DialogDescription>
        </DialogHeader>

        {finding ? (
          <div className="flex flex-col gap-3 text-sm">
            <DeceasedDetails evidence={finding.evidence} />
            <ul className="list-disc pl-5">
              <li>
                Adds the tag <code>{DECEASED_ARTIST_TAG}</code> to the artist.
              </li>
              <li>
                Blocks the artist&apos;s Spotify id, so Tina stops adding them
                to new events.
              </li>
              <li>Closes this card.</li>
            </ul>
            <p className="text-xs text-muted-foreground">
              It does not remove the artist from events that already list them.
              Open those events above to fix them by hand.
            </p>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={pending}>
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Approve as deceased
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
